import type { ShopperProductsTypes } from 'commerce-sdk-isomorphic';
import type { ProductPromotions } from '.';
import type { OptionalProps, RemoveIndex, RequiredProps } from '../../../types/helpers';
import type { ProductType, ShippingOptions } from '../../baskets/model';
import type { Badge } from './Badge';
import type { CustomDeliveryMessage } from './CustomDeliveryMessage';
import type { ProductVariationModel } from './ProductVariationModel';

export interface Prices {
    suggestedPrice?: number;
    regularPrice?: number;
    salePrice?: number;
}

export interface ProductVariant extends ShopperProductsTypes.Variant {
    orderable: boolean;
    price: number;
    productId: string;
    variationValues: {
        color?: string;
        colorName?: string;
        size?: string;
        isSingleSku?: 'yes' | 'no';
        gcDesign?: string;
        design?: string;
    };
}

export interface ProductVariationAttributeValue
    extends ShopperProductsTypes.VariationAttributeValue {
    name: string;
    value: string;
}

export interface ProductVariantAttribute extends ShopperProductsTypes.VariationAttribute {
    values: ProductVariationAttributeValue[];
}

interface CustomProductVariant {
    c_imageUrl: string;
    colorName: string;
    id: string;
    isOutOfStock: boolean;
    swatchColor: string | null;
}

export type ProductSavings = {
    amount?: number;
    percentage?: number;
};

export interface Product extends RequiredProps<RemoveIndex<ShopperProductsTypes.Product>, 'id'> {
    /**
     * Fields added by Site Genesis hook.
     */
    prices?: Prices;
    variants: ProductVariant[];
    variationAttributes?: ProductVariantAttribute[];
    c_variantIndex?: number;
    c_discount?: number;
    c_pwaUrl?: string;
    c_pageURL?: string;
    c_shippingOptions: ShippingOptions;
    c_primaryCategory?: ShopperProductsTypes.Category;
    c_promotions?: ProductPromotions;
    c_variationModel?: ProductVariationModel;
    c_productType?: ProductType;
    c_imageUrl?: string;
    c_plpImageUrl?: string;
    c_canonicalProductId?: string;
    c_availabilityStatus?: string;
    c_isExtendEligible?: boolean;

    /**
     * Custom fields.
     */
    c_dropShipEligibility?: boolean;
    c_dropShipMessage?: string;
    c_dropshipEstimatedDeliveryDate?: string;
    c_dropshipEstimatedProcessingDate?: string;
    c_badge?: Badge;
    c_additionalShippingCharges?: number;
    c_customDeliveryMessage?: CustomDeliveryMessage;
    c_brandShopUrl?: string;
    c_brandCategoryId?: string;
    c_youtubeVideoIds?: string;
    c_brandSpecialistVideoUrls?: string[];
    c_vendorID?: string;
    c_collectionId?: string;
    c_features?: string;
    c_specifications?: string;
    c_whatsInBox?: string;
    c_careusage?: string;
    c_turntoAverageRating?: string;
    c_turntoReviewCount?: number;
    c_turntoTotalReviewCount?: number;
    c_isBopisEligible?: boolean;
    c_isSingleSku?: 'yes' | 'no';
    c_landedCost?: string;
    c_badgeEndDate?: string;
    c_badgeName?: string;
    c_badgeStartDate?: string;
    c_capacity?: string;
    c_careAndUsage?: string;
    c_giftPack?: string;
    c_giftWrapPrice?: number;
    c_dateFilter?: string;
    c_gcType?: string;
    c_gcAmount?: number;
    c_gcPersonalMessage1?: string | null;
    c_gcPersonalMessage2?: string | null;
    c_gcPersonalMessage3?: string | null;
    c_gcPersonalMessage4?: string | null;
    giftMessage?: string | null;
    c_gcRecipientEmail?: string;
    c_gcRecipientName?: string;
    c_gcSenderName?: string;
    c_gcDeliveryDate?: string;
    c_online?: boolean;
    /**
     * Fields used only in this project.
     * They are not real custom fields.
     */
    c_quantityAddedToBag?: number;
    isApplePayElegible?: boolean;
    c_hazmatMessage?: string;
    c_legalMessage?: string;
    c_productSchema?: string | Record<string, unknown>;
    c_questionsSchema?: string;
    c_imageSchema?: string;
    c_organizationSchema?: string;
    isSaleFinal?: boolean;
    c_videoSchema?: Record<string, unknown>;
    basePrice?: number;
    c_containsPfa?: boolean;
    c_variants?: CustomProductVariant[];
    c_instructor?: string;

    /**
     * Fields added by BFF.
     */
    c_savings?: ProductSavings;
}

export type Products = Product[];

// #region Subtypes

export type NewProduct = OptionalProps<Product, 'id'>;

export interface OfflineProduct extends Product {
    c_online: false;
}

export function isOfflineProduct(product: Product): product is OfflineProduct {
    return product.c_online === false;
}

// #endregion
